import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, retry, timeout} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';

/**
 * Configuration interface for HTTP requests
 */
export interface ApiV2RequestConfig {
  timeout?: number;
  retries?: number;
  headers?: HttpHeaders | { [header: string]: string | string[] };
  params?: HttpParams | { [param: string]: string | string[] } | Record<string, any>;
}

/**
 * Universal HTTP service for REST API communication (API V2)
 *
 * Provides a clean, reusable HTTP layer for any backend resource:
 * - RESTful operations (GET, POST, PUT, PATCH, DELETE)
 * - Automatic timeout and retry handling
 * - Centralized error handling
 * - Query parameter building
 * - Configurable base URLs per resource
 *
 * This service can be used for any API endpoint, not just one specific resource.
 *
 * @example
 * ```TypeScript
 * // Using with a specific resource
 * constructor(private api: ApiV2Service) {}
 *
 * getUsers() {
 *   return this.api.get<User[]>('/users');
 * }
 *
 * // With query parameters
 * getUsers() {
 *   return this.api.get<User[]>('/users', { params: { page: 1, size: 20 } });
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ApiV2Service {
  private readonly DEFAULT_BASE_URL: string = environment.baseUrl;
  private readonly DEFAULT_TIMEOUT: number = 30000; // 30 seconds
  private readonly DEFAULT_RETRIES: number = 2;

  constructor(
    private readonly http: HttpClient
  ) {
  }

  /**
   * Executes a GET request
   *
   * @param url - Full URL or relative path (will be prefixed with baseUrl)
   * @param config - Optional request configuration
   * @returns Observable of the response
   *
   * @example
   * ```TypeScript
   * // Relative path (uses environment.baseUrl)
   * this.api.get<User[]>('/users')
   *   .subscribe(users => console.log(users));
   *
   * // Full URL
   * this.api.get<Data>('https://api.example.com/data')
   *   .subscribe(data => console.log(data));
   *
   * // With params
   * this.api.get<User[]>('/users', { params: { page: 1, size: 20 } })
   *   .subscribe(users => console.log(users));
   * ```
   */
  public get<T>(url: string, config?: ApiV2RequestConfig): Observable<T> {
    const fullUrl: string = this.buildFullUrl(url);
    const options = this.buildRequestOptions(config);

    return this.http.get<T>(fullUrl, options)
      .pipe(
        timeout(config?.timeout ?? this.DEFAULT_TIMEOUT),
        retry(config?.retries ?? this.DEFAULT_RETRIES),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Executes a POST request
   *
   * @param url - Full URL or relative path
   * @param body - Request body
   * @param config - Optional request configuration
   * @returns Observable of the response
   *
   * @example
   * ```TypeScript
   * this.api.post<User>('/users', { name: 'John', email: 'john@example.com' })
   *   .subscribe(created => console.log('Created:', created));
   * ```
   */
  public post<T>(url: string, body: any, config?: ApiV2RequestConfig): Observable<T> {
    const fullUrl: string = this.buildFullUrl(url);
    const options = this.buildRequestOptions(config);

    return this.http.post<T>(fullUrl, body, options)
      .pipe(
        timeout(config?.timeout ?? this.DEFAULT_TIMEOUT),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Executes a PUT request (full update)
   *
   * @param url - Full URL or relative path
   * @param body - Request body
   * @param config - Optional request configuration
   * @returns Observable of the response
   *
   * @example
   * ```TypeScript
   * this.api.put<User>('/users/123', { name: 'John Updated', email: 'john@example.com' })
   *   .subscribe(updated => console.log('Updated:', updated));
   * ```
   */
  public put<T>(url: string, body: any, config?: ApiV2RequestConfig): Observable<T> {
    const fullUrl: string = this.buildFullUrl(url);
    const options = this.buildRequestOptions(config);

    return this.http.put<T>(fullUrl, body, options)
      .pipe(
        timeout(config?.timeout ?? this.DEFAULT_TIMEOUT),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Executes a PATCH request (partial update)
   *
   * @param url - Full URL or relative path
   * @param body - Request body (partial data)
   * @param config - Optional request configuration
   * @returns Observable of the response
   *
   * @example
   * ```TypeScript
   * this.api.patch<User>('/users/123', { name: 'New Name' })
   *   .subscribe(patched => console.log('Patched:', patched));
   * ```
   */
  public patch<T>(url: string, body: any, config?: ApiV2RequestConfig): Observable<T> {
    const fullUrl: string = this.buildFullUrl(url);
    const options = this.buildRequestOptions(config);

    return this.http.patch<T>(fullUrl, body, options)
      .pipe(
        timeout(config?.timeout ?? this.DEFAULT_TIMEOUT),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Executes a DELETE request
   *
   * @param url - Full URL or relative path
   * @param config - Optional request configuration
   * @returns Observable of the response
   *
   * @example
   * ```TypeScript
   * this.api.delete<void>('/users/123')
   *   .subscribe(() => console.log('Deleted'));
   * ```
   */
  public delete<T>(url: string, config?: ApiV2RequestConfig): Observable<T> {
    const fullUrl: string = this.buildFullUrl(url);
    const options = this.buildRequestOptions(config);

    return this.http.delete<T>(fullUrl, options)
      .pipe(
        timeout(config?.timeout ?? this.DEFAULT_TIMEOUT),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Builds query parameters from an object
   * Filters out null and undefined values
   *
   * @param params - Query parameters object
   * @returns HttpParams instance
   *
   * @example
   * ```TypeScript
   * const params = this.api.buildParams({ page: 1, size: 20, search: 'test' });
   * // Returns HttpParams with ?page=1&size=20&search=test
   * ```
   */
  public buildParams(params?: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();

    if (!params) {
      return httpParams;
    }

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  /**
   * Builds the full URL from a relative or absolute path
   * If the URL starts with http:// or https://, returns it as-is
   * Otherwise, prepends the environment base URL
   *
   * @param url - URL or path
   * @returns Full URL
   * @private
   */
  private buildFullUrl(url: string): string {
    // If it's already a full URL, return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // If it starts with /, it's an absolute path from base URL
    if (url.startsWith('/')) {
      return `${this.DEFAULT_BASE_URL}${url}`;
    }

    // Otherwise, append to base URL with /
    return `${this.DEFAULT_BASE_URL}/${url}`;
  }

  /**
   * Builds request options from configuration
   * Automatically converts plain objects to HttpParams
   *
   * @param config - Request configuration
   * @returns HTTP request options
   * @private
   */
  private buildRequestOptions(config?: ApiV2RequestConfig): {
    headers?: HttpHeaders | { [header: string]: string | string[] };
    params?: HttpParams | { [param: string]: string | string[] };
  } {
    const options: any = {};

    if (config?.headers) {
      options.headers = config.headers;
    }

    if (config?.params) {
      // If params is a plain object (not HttpParams), convert it
      if (!(config.params instanceof HttpParams) && this.isPlainObject(config.params)) {
        options.params = this.buildParams(config.params as Record<string, any>);
      } else {
        options.params = config.params;
      }
    }

    return options;
  }

  /**
   * Checks if a value is a plain object
   *
   * @param value - Value to check
   * @returns True if value is a plain object
   * @private
   */
  private isPlainObject(value: any): boolean {
    return value !== null && typeof value === 'object' && value.constructor === Object;
  }

  /**
   * Centralized error handling for HTTP requests
   * Transforms HTTP errors into user-friendly error messages
   *
   * @param error - HTTP error response
   * @returns Observable that throws a formatted error
   * @private
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage: string;

    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      errorMessage = `Network error: ${error.error.message}`;
    } else {
      // Backend error
      switch (error.status) {
        case 400:
          errorMessage = `Bad Request: ${error.error?.message || 'Invalid data provided'}`;
          break;
        case 401:
          errorMessage = 'Unauthorized: Please log in to continue';
          break;
        case 403:
          errorMessage = 'Forbidden: You do not have permission to perform this action';
          break;
        case 404:
          errorMessage = `Not Found: ${error.error?.message || 'The requested resource does not exist'}`;
          break;
        case 409:
          errorMessage = `Conflict: ${error.error?.message || 'A resource with this identifier already exists'}`;
          break;
        case 422:
          errorMessage = `Validation Error: ${error.error?.message || 'Invalid data configuration'}`;
          break;
        case 500:
          errorMessage = 'Internal Server Error: Please try again later';
          break;
        case 503:
          errorMessage = 'Service Unavailable: The server is temporarily unavailable';
          break;
        default:
          errorMessage = `Unexpected error (${error.status}): ${error.error?.message || error.message}`;
      }
    }

    console.error('ApiV2Service Error:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }
}

